import * as Sentry from '@sentry/react';

import { env } from './env';

/** Escapes regex metacharacters so a URL or path can be safely embedded in a RegExp. */
const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only runs in dev, at, and prod environments
 */
export const initSentry = (): void => {
  if (!env.sentry.enabled) {
    return;
  }

  Sentry.init({
    dsn: env.sentry.dsn,
    tunnel: '/sentry-tunnel',
    release: env.sentry.release,
    environment: env.environment,
    // Both are intentional cancellations: TanStack Query aborts an in-flight fetch when a query loses
    // its last observer, is invalidated mid-flight, or logout cancels the cache. Nothing to fix.
    ignoreErrors: ['CancelledError', 'AbortError'],
    // API error bodies sit four to five levels deep under `extra` (e.g. `extra.apiError.error.errors.code`).
    // The default depth of 3 replaced them with "[Object]"; six levels keep the whole body readable.
    normalizeDepth: 6,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false
      })
    ],
    // Performance Monitoring, set to 0.1 to reduce performance overhead and avoid billing overage.
    tracesSampleRate: 0.1,
    // Set tracePropagationTargets to control for which URLs distributed tracing should be enabled.
    // The API base is anchored to the START of the URL: Sentry matches plain strings as
    // substrings, so a relative base like `/api` also matches third-party URLs that merely
    // contain it (e.g. https://maps.googleapis.com/maps/api/…). The injected sentry-trace/
    // baggage headers turn those simple requests into CORS preflights third parties reject,
    // which broke Google Maps loading on deployed environments.
    tracePropagationTargets: ['localhost', new RegExp(`^${escapeForRegExp(env.apiBaseUrl)}`)],
    // Errors Session Replay
    replaysSessionSampleRate: 0, // Disable non-error session replays explicitly
    replaysOnErrorSampleRate: 1.0 // Always capture sessions with errors
  });
};
